import React from "react";
import { Box, Text, IconButton, Tooltip, Icon } from "@chakra-ui/react";
import { MdContentCopy, MdCheckCircle, MdOpenInNew } from "react-icons/md";

/**
 * Renders a styled info row with optional icon, chip display, copy button, and invoice link.
 * Used to display reservation and contact card details.
 * @param {Object} props
 * @param {React.ReactElement} [props.icon] - Icon element (react-icons)
 * @param {string} props.label - Label text
 * @param {string|React.ReactNode} props.value - Value content
 * @param {string} [props.valueColor] - Custom color for the value text
 * @param {boolean} [props.chip] - Whether to render value as a chip
 * @param {Object} [props.chipColor] - Chip color config: { bg, color, border }
 * @param {boolean} [props.copyable] - Whether to show a copy button
 * @param {boolean} [props.isCopied] - Whether the copy action was triggered
 * @param {Function} [props.onCopy] - Copy button click handler
 * @param {boolean} [props.isInvoice] - Whether to show an invoice open button
 * @param {string} [props.invoiceUrl] - URL to open for invoice
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ContactInfoRow({ icon, label, value, valueColor, chip, chipColor, copyable, isCopied, onCopy, isInvoice, invoiceUrl, isMobile }) {
    return (
        <Box {...rowContainerProps(isMobile)}>
            {icon && React.cloneElement(icon, {
                color: "#17dcb8", boxSize: isMobile ? "16px" : "18px", flexShrink: 0
            })}
            <Text {...labelProps(isMobile)}>
                {label}
            </Text>
            {chip && chipColor
                ? <ChipValue value={value} chipColor={chipColor} isMobile={isMobile} />
                : <TextValue value={value} valueColor={valueColor} isMobile={isMobile} />
            }
            {isInvoice && invoiceUrl && (
                <InvoiceButton invoiceUrl={invoiceUrl} />
            )}
            {copyable && (
                <CopyButton isCopied={isCopied} onCopy={onCopy} />
            )}
        </Box>
    );
}

/**
 * Renders the value as a styled chip box.
 * @param {Object} props
 * @param {string} props.value - Chip text
 * @param {Object} props.chipColor - Color config: { bg, color, border }
 * @param {boolean} props.isMobile - Mobile flag
 * @returns {React.ReactElement}
 */
function ChipValue({ value, chipColor, isMobile }) {
    return (
        <Box
            padding="4px 12px"
            background={chipColor.bg}
            backdropFilter="blur(10px)"
            color={chipColor.color}
            fontWeight={600}
            fontSize={isMobile ? "0.7rem" : "0.75rem"}
            height="26px"
            borderRadius="8px"
            border={`1px solid ${chipColor.border}`}
            boxShadow={`0 2px 8px ${chipColor.bg}`}
            display="flex"
            alignItems="center"
            marginLeft="auto"
        >
            {value}
        </Box>
    );
}

/**
 * Renders the value as plain text.
 * @param {Object} props
 * @param {string|React.ReactNode} props.value - Value content
 * @param {string} [props.valueColor] - Custom text color
 * @param {boolean} props.isMobile - Mobile flag
 * @returns {React.ReactElement}
 */
function TextValue({ value, valueColor, isMobile }) {
    return (
        <Text
            color={valueColor || "rgba(255, 255, 255, 0.85)"}
            fontSize={isMobile ? "0.75rem" : "0.8rem"}
            fontWeight={500}
            flex={1}
            textAlign="right"
            wordBreak="break-word"
        >
            {value || "Non renseigne"}
        </Text>
    );
}

/**
 * Renders the open-invoice icon button.
 * @param {Object} props
 * @param {string} props.invoiceUrl - URL to open
 * @returns {React.ReactElement}
 */
function InvoiceButton({ invoiceUrl }) {
    return (
        <Tooltip label="Ouvrir la facture" hasArrow>
            <IconButton
                aria-label="Ouvrir la facture"
                size="sm"
                onClick={() => window.open(invoiceUrl, '_blank')}
                icon={<Icon as={MdOpenInNew} boxSize="18px" />}
                {...invoiceButtonProps}
            />
        </Tooltip>
    );
}

/**
 * Renders the copy-to-clipboard icon button.
 * @param {Object} props
 * @param {boolean} props.isCopied - Whether copy was just triggered
 * @param {Function} props.onCopy - Click handler
 * @returns {React.ReactElement}
 */
function CopyButton({ isCopied, onCopy }) {
    return (
        <Tooltip label={isCopied ? "Copie !" : "Copier"} hasArrow>
            <IconButton
                aria-label="Copier"
                size="sm"
                onClick={onCopy}
                icon={<Icon as={isCopied ? MdCheckCircle : MdContentCopy} boxSize="18px" />}
                {...copyButtonProps(isCopied)}
            />
        </Tooltip>
    );
}

/**
 * Returns style props for the row container.
 * @param {boolean} isMobile - Mobile flag
 * @returns {Object} Chakra style props
 */
function rowContainerProps(isMobile) {
    return {
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: isMobile ? "6px 10px" : "7px 12px",
        marginBottom: "8px",
        background: "rgba(255, 255, 255, 0.04)",
        backdropFilter: "blur(10px)",
        borderRadius: "8px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        transition: "all 0.3s ease",
        _hover: {
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.12)"
        }
    };
}

/**
 * Returns style props for the label text.
 * @param {boolean} isMobile - Mobile flag
 * @returns {Object} Chakra style props
 */
function labelProps(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.6)",
        fontSize: isMobile ? "0.65rem" : "0.7rem",
        fontWeight: 600,
        minWidth: isMobile ? "70px" : "80px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        flexShrink: 0
    };
}

/** Style props for the invoice open button */
const invoiceButtonProps = {
    color: "rgba(33, 150, 243, 1)",
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    minW: "auto",
    h: "auto",
    padding: "6px",
    _hover: {
        background: "rgba(33, 150, 243, 0.15)",
        border: "1px solid rgba(33, 150, 243, 0.3)"
    }
};

/**
 * Returns style props for the copy button based on copied state.
 * @param {boolean} isCopied - Whether the copy was just performed
 * @returns {Object} Chakra style props
 */
function copyButtonProps(isCopied) {
    return {
        color: isCopied ? "rgba(76, 175, 80, 1)" : "rgba(255, 255, 255, 0.6)",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        minW: "auto",
        h: "auto",
        padding: "6px",
        _hover: {
            background: "rgba(255, 255, 255, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.15)"
        }
    };
}

export default ContactInfoRow;
